import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface WordmarkProps {
    name: string
    className?: string
}

const Glyph: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => (
    <svg viewBox='0 0 20 20' fill='none' aria-hidden='true' className={cn('h-[1.05em] w-[1.05em] shrink-0', className)}>
        {children}
    </svg>
)

// Typographic logos for the fictional customer companies. Every mark inherits the surrounding text color.
const marks: Record<string, () => ReactNode> = {
    'Northgate': () => (
        <>
            <Glyph>
                <path d='M10 3.2 17.6 16.8H2.4Z' fill='currentColor' />
            </Glyph>

            <span className='text-[0.8em] font-semibold uppercase tracking-[0.22em]'>Northgate</span>
        </>
    ),
    'Halden & Co': () => (
        <span className='font-serif text-[1.05em] italic tracking-[-0.01em]'>Halden &amp; Co</span>
    ),
    'Brightline': () => (
        <>
            <Glyph>
                <circle cx='10' cy='8' r='4.2' fill='currentColor' />
                <rect x='2' y='14.6' width='16' height='2.2' rx='1.1' fill='currentColor' />
            </Glyph>

            <span className='font-bold lowercase tracking-[-0.04em]'>brightline</span>
        </>
    ),
    'Kestrel Freight': () => (
        <>
            <Glyph>
                <path d='M4 16 9.5 4h3L7 16Z M10 16 15.5 4h2.5L12.5 16Z' fill='currentColor' />
            </Glyph>

            <span className='text-[0.86em] uppercase tracking-[0.08em]'>
                <span className='font-extrabold'>Kestrel</span>
                {' '}
                <span className='font-normal'>Freight</span>
            </span>
        </>
    ),
    'Oakmont Supply': () => (
        <>
            <Glyph>
                <path d='M10 2.5 16.5 6.25v7.5L10 17.5 3.5 13.75v-7.5Z' stroke='currentColor' strokeWidth='2' />
                <circle cx='10' cy='10' r='2.2' fill='currentColor' />
            </Glyph>

            <span className='tracking-[-0.03em]'>
                <span className='font-semibold'>Oakmont</span>
                {' '}
                <span className='font-normal opacity-80'>Supply</span>
            </span>
        </>
    ),
    'Pallet': () => (
        <>
            <Glyph>
                <rect x='3' y='4' width='14' height='2.6' rx='1.3' fill='currentColor' />
                <rect x='3' y='8.7' width='14' height='2.6' rx='1.3' fill='currentColor' />
                <rect x='3' y='13.4' width='14' height='2.6' rx='1.3' fill='currentColor' />
            </Glyph>

            <span className='font-extrabold lowercase tracking-[-0.05em]'>pallet</span>
        </>
    ),
    'Lumen': () => (
        <>
            <Glyph>
                <circle cx='10' cy='10' r='6.5' stroke='currentColor' strokeWidth='2.4' />
            </Glyph>

            <span className='text-[0.86em] font-light uppercase tracking-[0.32em]'>Lumen</span>
        </>
    ),
    'Arbor Goods': () => (
        <span className='font-serif tracking-[-0.01em]'>
            <span className='font-semibold'>Arbor</span>
            {' '}
            <span className='text-[0.68em] uppercase tracking-[0.24em]'>Goods</span>
        </span>
    ),
    'Tessel': () => (
        <>
            <Glyph>
                <rect x='3' y='3' width='6' height='6' rx='1.2' fill='currentColor' />
                <rect x='11' y='3' width='6' height='6' rx='3' fill='currentColor' />
                <rect x='3' y='11' width='6' height='6' rx='3' fill='currentColor' />
                <rect x='11' y='11' width='6' height='6' rx='1.2' fill='currentColor' />
            </Glyph>

            <span className='font-medium lowercase tracking-[-0.02em]'>tessel</span>
        </>
    ),
    'Vantage Field': () => (
        <span className='font-mono text-[0.78em] font-medium uppercase tracking-[0.06em]'>
            Vantage
            <span className='mx-[0.3em] opacity-50'>/</span>
            Field
        </span>
    ),
    'Ridgeway': () => (
        <>
            <Glyph>
                <path d='M1.5 16 7 7l3.5 5L13 8.5 18.5 16' stroke='currentColor' strokeWidth='2.2' strokeLinejoin='round' strokeLinecap='round' />
            </Glyph>

            <span className='font-bold tracking-[-0.03em]'>Ridgeway</span>
        </>
    ),
    'Copperleaf': () => (
        <>
            <Glyph>
                <path d='M4 16C4 9 8.5 4 16.5 3.5 16 11.5 11 16 4 16Z' fill='currentColor' />
            </Glyph>

            <span className='font-semibold lowercase italic tracking-[-0.03em]'>copperleaf</span>
        </>
    ),
    'Sable Coffee': () => (
        <span className='font-serif tracking-[0.02em]'>
            <span className='font-bold uppercase'>Sable</span>
            {' '}
            <span className='text-[0.9em] italic opacity-80'>coffee</span>
        </span>
    ),
    'Mosaic Health': () => (
        <>
            <Glyph>
                <circle cx='10' cy='4.5' r='2.4' fill='currentColor' />
                <circle cx='4.5' cy='10' r='2.4' fill='currentColor' />
                <circle cx='15.5' cy='10' r='2.4' fill='currentColor' />
                <circle cx='10' cy='15.5' r='2.4' fill='currentColor' />
            </Glyph>

            <span className='lowercase tracking-[-0.03em]'>
                <span className='font-semibold'>mosaic</span>
                <span className='font-normal opacity-75'>health</span>
            </span>
        </>
    ),
    'Quarry': () => (
        <span className='font-black uppercase tracking-[-0.04em]'>Quarry</span>
    ),
    'Helix Parts': () => (
        <>
            <Glyph>
                <path d='M5 2.5c0 5 10 5 10 7.5S5 12.5 5 17.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
                <path d='M15 2.5c0 5-10 5-10 7.5s10 2.5 10 7.5' stroke='currentColor' strokeWidth='2' strokeLinecap='round' opacity='0.55' />
            </Glyph>

            <span className='lowercase tracking-[-0.03em]'>
                <span className='font-bold'>helix</span>
                {' '}
                <span className='font-normal'>parts</span>
            </span>
        </>
    ),
    'Wexford Rail Models': () => (
        <span className='inline-flex items-baseline gap-[0.4em] font-serif'>
            <span className='font-bold tracking-[0.01em]'>Wexford</span>
            <span className='text-[0.6em] font-medium uppercase tracking-[0.26em] opacity-80'>Rail Models</span>
        </span>
    )
}

export const Wordmark: FC<WordmarkProps> = ({ name, className }): ReactNode => {
    const mark = marks[name]

    return (
        <span role='img' aria-label={name} className={cn('inline-flex shrink-0 items-center gap-[0.4em] whitespace-nowrap leading-none', className)}>
            <span aria-hidden='true' className='inline-flex items-center gap-[0.4em]'>
                {mark ? mark() : <span className='font-semibold tracking-[-0.03em]'>{name}</span>}
            </span>
        </span>
    )
}
